import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { IconNames, PERMISSION_CONFIG, PermissionConfig } from '../../common/constants';
import { IntrackFeatureModule } from '../../intrack-feature.module';
import { ActionButtonComponent } from '../action-button';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { AuthFacade } from '../../store/state/auth';
import { DeliveryNoteTypeView } from '../../store/state/monitoring/automotive-aftermarket';
import { ProofOfDeliveryFacadeType } from '../../common/models';
import { PublicProofOfDeliveryStateModel } from '../../store/state/opened-resources';
import { ValidationService } from '../../common/services';
import { AutoUnsubscribe } from '../../common/decorators';
import { ButtonAction, ButtonType } from '../action-button/constants';
import {
  DeliveryNoteProofOfDeliveryStateModel,
  ProofOfDelivery,
  ProofOfDeliveryItem,
  ProofOfDeliveryItemFileForm,
  ProofOfDeliveryItemForm,
} from '../../store/state/delivery-note';
import { FormArray, FormBuilder, FormGroup, UntypedFormArray } from '@angular/forms';
import { Subscription } from 'rxjs';
import { difference, flatten, isNil, omitBy } from 'lodash';

@Component({
  selector: 'ui-proof-of-delivery-base',
  imports: [IntrackFeatureModule, ActionButtonComponent, TranslateModule, SvgIconComponent],
  templateUrl: './proof-of-delivery-base.component.html',
  styleUrls: ['./proof-of-delivery-base.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
@AutoUnsubscribe
export class ProofOfDeliveryBaseComponent implements OnInit {
  @Input() proofOfDeliveryFacade: ProofOfDeliveryFacadeType;
  @Input() token?: string;
  @Input() isPermissionsApplicable = true;

  buttonAction = ButtonAction;
  proofOfDeliveryItemForm: FormGroup<ProofOfDeliveryItemForm>;
  proofOfDeliveryDocumentsFormArray: FormArray<FormGroup<ProofOfDeliveryItemFileForm>>;
  proofOfDeliveryDocumentsFormInitialValue: Partial<ProofOfDelivery>[];
  proofOfDeliveryTypeList: DeliveryNoteTypeView[] = Object.values(DeliveryNoteTypeView);
  subscriptions: Subscription[] = [];
  permissionConfig: PermissionConfig = PERMISSION_CONFIG;
  buttonType = ButtonType;
  huNumbers: string[] = [];
  allHandlingUnits: string[] = [];
  proofOfDeliveryType = DeliveryNoteTypeView;
  alreadyUsedHandlingUnits: string[] = [];
  proofOfDeliveryData: DeliveryNoteProofOfDeliveryStateModel & PublicProofOfDeliveryStateModel;
  iconNameList = IconNames;

  get isHandlingUnitType(): boolean {
    return this.proofOfDeliveryItemForm?.get('type').value === this.proofOfDeliveryType.HandlingUnit;
  }
  get isAllHandlingUnitsChecked(): boolean {
    return (
      this.proofOfDeliveryDocumentsFormArray.controls?.length > 1 &&
      this.allHandlingUnits?.length === this.alreadyUsedHandlingUnits?.length
    );
  }
  private proofOfDeliveryTypeSubscription: Subscription;
  private getAlreadyUsedHandlingUnitsSubscription: Subscription;
  private readonly huNumberValueForDeliveryNoteType = 'All';

  constructor(
    public authFacade: AuthFacade,
    public validationService: ValidationService,
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(this.proofOfDeliveryItemDataSubscription());
  }

  addProofOfDeliveryFile(): void {
    let group: FormGroup<ProofOfDeliveryItemFileForm>;
    if (!this.isHandlingUnitType) {
      group = this.fb.group<ProofOfDeliveryItemFileForm>(
        new ProofOfDeliveryItemFileForm(new ProofOfDelivery({ huNumbers: [this.huNumberValueForDeliveryNoteType] })),
      );
      this.huNumbers = [this.huNumberValueForDeliveryNoteType];
      group.get('huNumbers').disable();
    } else {
      group = this.fb.group<ProofOfDeliveryItemFileForm>(new ProofOfDeliveryItemFileForm(new ProofOfDelivery()));
    }
    this.proofOfDeliveryDocumentsFormArray.push(group);
  }

  onAddFile(file: File, index: number): void {
    const reader: FileReader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (): void => {
      const content: string = btoa(reader.result as string);
      this.proofOfDeliveryDocumentsFormArray.at(index).patchValue({ content, name: file.name, contentType: file.type });
      if (this.proofOfDeliveryItemForm.pristine) {
        this.proofOfDeliveryItemForm.markAsDirty();
      }
      this.cd.detectChanges();
    };
  }

  createOrUpdateProofOfDelivery(): void {
    const proofOfDeliveryItemValue = this.proofOfDeliveryItemForm.getRawValue();
    proofOfDeliveryItemValue.documents = proofOfDeliveryItemValue.documents.map(
      (document: ProofOfDelivery) => omitBy(document, isNil) as ProofOfDelivery,
    );
    if (!this.isHandlingUnitType) {
      proofOfDeliveryItemValue.documents.forEach((document: ProofOfDelivery) => {
        if (document?.huNumbers) {
          document.huNumbers = this.allHandlingUnits;
        }
      });
    }
    const token: string = this.token;
    const params = { proofOfDeliveryItemValue, token };
    this.proofOfDeliveryFacade.saveProofOfDeliveryItem(params);
  }

  deleteProofOfDeliveryDocumentOrFile(index: number): void {
    this.proofOfDeliveryDocumentsFormArray.removeAt(index);
    const { value: targetValue } = this.proofOfDeliveryDocumentsFormArray;
    const isArraysNotEqual: boolean =
      JSON.stringify(this.proofOfDeliveryDocumentsFormInitialValue) !== JSON.stringify(targetValue);
    if (isArraysNotEqual) {
      this.proofOfDeliveryItemForm.markAsDirty();
    } else {
      this.proofOfDeliveryItemForm.markAsPristine();
    }
  }

  downloadFile(documentId: string, name: string): void {
    const token: string = this.token;
    const params = { documentId, token, name };
    this.proofOfDeliveryFacade.downloadFile(params);
  }

  private proofOfDeliveryItemDataSubscription(): Subscription {
    return this.proofOfDeliveryFacade.proofOfDeliveryItemData$.subscribe(
      (data: DeliveryNoteProofOfDeliveryStateModel & PublicProofOfDeliveryStateModel) => {
        this.proofOfDeliveryData = data;
        if (this.proofOfDeliveryItemForm) {
          this.proofOfDeliveryItemForm.reset();
          this.allHandlingUnits = [];
          this.alreadyUsedHandlingUnits = [];
        }
        if (this.proofOfDeliveryTypeSubscription) {
          this.proofOfDeliveryTypeSubscription.unsubscribe();
        }
        if (this.getAlreadyUsedHandlingUnitsSubscription) {
          this.getAlreadyUsedHandlingUnitsSubscription.unsubscribe();
        }
        this.huNumbers = data.allHandlingUnits;
        this.allHandlingUnits = data.allHandlingUnits;
        data && data.proofOfDeliveryView && data.loaded
          ? this.initUpdateProofOfDeliveryItemForm(data)
          : this.initCreateProofOfDeliveryItemForm();
        this.initProofOfDeliveryTypeSubscription();
        this.getAlreadyUsedHandlingUnits();
      },
    );
  }

  private initCreateProofOfDeliveryItemForm(): void {
    this.proofOfDeliveryItemForm = this.fb.group<ProofOfDeliveryItemForm>(
      new ProofOfDeliveryItemForm(new ProofOfDeliveryItem()),
    );
    this.proofOfDeliveryDocumentsFormArray = this.proofOfDeliveryItemForm.get('documents') as UntypedFormArray;
    this.huNumbers = [this.huNumberValueForDeliveryNoteType];
    this.proofOfDeliveryDocumentsFormArray.push(
      this.fb.group<ProofOfDeliveryItemFileForm>(
        new ProofOfDeliveryItemFileForm(new ProofOfDelivery({ huNumbers: [this.huNumberValueForDeliveryNoteType] })),
      ),
    );
    this.proofOfDeliveryDocumentsFormArray.controls.forEach((control: FormGroup<ProofOfDeliveryItemFileForm>) => {
      control.get('huNumbers').disable();
    });
  }

  private initUpdateProofOfDeliveryItemForm(data: DeliveryNoteProofOfDeliveryStateModel): void {
    this.proofOfDeliveryItemForm = this.fb.group<ProofOfDeliveryItemForm>(
      new ProofOfDeliveryItemForm(new ProofOfDeliveryItem(data)),
    );
    this.proofOfDeliveryDocumentsFormArray = this.proofOfDeliveryItemForm.get('documents') as UntypedFormArray;
    data.proofOfDeliveryView?.documents.forEach((document: ProofOfDelivery) => {
      this.alreadyUsedHandlingUnits.push(...document.huNumbers);
      this.proofOfDeliveryDocumentsFormArray.push(
        this.fb.group<ProofOfDeliveryItemFileForm>(new ProofOfDeliveryItemFileForm(new ProofOfDelivery(document))),
      );
    });
    if (this.isDeliveryNoteTypeShouldBeApplied()) {
      this.proofOfDeliveryItemForm.get('type').setValue(DeliveryNoteTypeView.DeliveryNote);
      this.huNumbers = [this.huNumberValueForDeliveryNoteType];
      this.proofOfDeliveryDocumentsFormArray.controls.forEach((control: FormGroup<ProofOfDeliveryItemFileForm>) => {
        control.get('huNumbers').setValue([this.huNumberValueForDeliveryNoteType]);
        control.get('huNumbers').disable();
      });
    } else {
      this.proofOfDeliveryItemForm.get('type').setValue(DeliveryNoteTypeView.HandlingUnit);
    }
    this.proofOfDeliveryDocumentsFormInitialValue = this.proofOfDeliveryDocumentsFormArray.value;
  }

  private getAlreadyUsedHandlingUnits(): void {
    this.getAlreadyUsedHandlingUnitsSubscription = this.proofOfDeliveryDocumentsFormArray.valueChanges.subscribe(
      (array: ProofOfDelivery[]) => {
        this.alreadyUsedHandlingUnits = flatten(array.map(({ huNumbers }: ProofOfDelivery) => huNumbers));
        if (
          this.isHandlingUnitType &&
          this.proofOfDeliveryDocumentsFormArray.controls?.length === 1 &&
          this.allHandlingUnits?.length === this.alreadyUsedHandlingUnits?.length
        ) {
          this.proofOfDeliveryItemForm.get('type').setValue(DeliveryNoteTypeView.DeliveryNote);
        }
      },
    );
    this.subscriptions.push(this.getAlreadyUsedHandlingUnitsSubscription);
  }

  private initProofOfDeliveryTypeSubscription(): void {
    this.proofOfDeliveryTypeSubscription = this.proofOfDeliveryItemForm
      .get('type')
      .valueChanges.subscribe((type: DeliveryNoteTypeView) => {
        if (type === DeliveryNoteTypeView.DeliveryNote) {
          this.proofOfDeliveryDocumentsFormArray.clear();
          this.huNumbers = [this.huNumberValueForDeliveryNoteType];
          const group: FormGroup<ProofOfDeliveryItemFileForm> = this.fb.group<ProofOfDeliveryItemFileForm>(
            new ProofOfDeliveryItemFileForm(
              new ProofOfDelivery({ huNumbers: [this.huNumberValueForDeliveryNoteType] }),
            ),
          );
          group.get('huNumbers').disable();
          this.proofOfDeliveryDocumentsFormArray.push(group);
        } else {
          this.proofOfDeliveryDocumentsFormArray.clear();
          this.huNumbers = this.allHandlingUnits;
          const group: FormGroup<ProofOfDeliveryItemFileForm> = this.fb.group<ProofOfDeliveryItemFileForm>(
            new ProofOfDeliveryItemFileForm(new ProofOfDelivery()),
          );
          this.proofOfDeliveryDocumentsFormArray.push(group);
        }
      });
    this.subscriptions.push(this.proofOfDeliveryTypeSubscription);
  }

  private isDeliveryNoteTypeShouldBeApplied(): boolean {
    return this.proofOfDeliveryDocumentsFormArray.controls?.every((control: FormGroup<ProofOfDeliveryItemFileForm>) => {
      return difference(this.allHandlingUnits, control.value?.huNumbers).length === 0;
    });
  }
}
